import React, {useState, useEffect} from 'react';
import {useRecoilState, useRecoilValue, useSetRecoilState} from 'recoil';
import {userState} from '../../../../recoil/user/userAtom';
import {useHistory} from 'react-router-dom';

import {reloadRfpsState} from '../../../../recoil/reloadRfps/reloadRfpsAtom';
import {removeHeaderState} from '../../../../recoil/removeAppBar/removeAppBarAtom';

import {ItemsContractor} from './ItemsContractor';
import {ItemsHomeowner} from './ItemsHomeowner';
import {ItemsAnonymous} from './ItemsAnonymous';
import {NavigationDrawer} from './NavigationDrawer';
import {
  feedbackDialogOpenState,
  nonLoggedInUserDialogOpenState,
  supportPopupOpenState,
} from '../../../../recoil/dialogs/DialogsRecoil';

import {
  getSelectedIndexFromCurrentUrl,
  NavigationPath,
} from '../SelectedIndexUtil';
import {isContractor, isHomeowner, isRealtor} from '../../../../util/UserUtils';
import {savedProjectsFilterState} from '../../../../recoil/filterValues/filterValuesAtom';
import * as Sentry from '@sentry/react';
import {signOut} from 'firebase/auth';
import {firebaseAuth} from '../../../../App';

interface IProps {
  width: number;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
}

export const LeftNavigation: React.FC<IProps> = (props) => {
  const {width} = props;
  const history = useHistory();
  const removeHeader = useRecoilValue(removeHeaderState);

  const setNonLoggedInUserDialogOpen = useSetRecoilState(
    nonLoggedInUserDialogOpenState,
  );
  const setFeedbackDialogOpen = useSetRecoilState(feedbackDialogOpenState);
  const setSupportPopupOpen = useSetRecoilState(supportPopupOpenState);
  const setReloadRfps = useSetRecoilState(reloadRfpsState);
  const [user, setUser] = useRecoilState(userState);
  const setSavedProjectsFilter = useSetRecoilState(savedProjectsFilterState);
  const [selectedIndex, setSelectedIndex] = useState(NavigationPath.DEFAULT);

  useEffect(() => {
    setSelectedIndex(getSelectedIndexFromCurrentUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [window.location.pathname]);

  const closeMobileDrawer = () => {
    if (width <= 600) {
      props.setDrawerOpen(false);
      setTimeout(() => {
        if (props.drawerOpen) {
          props.setDrawerOpen(false);
        }
      }, 500);
    }
  };

  const handleListItemClick = (navigationPath: NavigationPath) => {
    if (
      (!user || localStorage.getItem('isAnonymous')) &&
      navigationPath !== NavigationPath.COMMUNITY_RFPS &&
      navigationPath !== NavigationPath.HELP_ARTICLES &&
      navigationPath !== NavigationPath.FAQ &&
      navigationPath !== NavigationPath.BIDMII_PRO &&
      navigationPath !== NavigationPath.SUBSCRIPTIONS &&
      navigationPath !== NavigationPath.CDAP &&
      navigationPath !== NavigationPath.CONTRACTOR_SEARCH &&
      navigationPath !== NavigationPath.RESOURCES &&
      !navigationPath.includes('/m/')
    ) {
      setNonLoggedInUserDialogOpen(true);
      return;
    }
    if (navigationPath === NavigationPath.FEEDBACK) {
      setFeedbackDialogOpen(true);
      return;
    }
    if (navigationPath === NavigationPath.CONTACT) {
      setSupportPopupOpen(true);
      closeMobileDrawer();
      return;
    }
    const div = document.getElementById('infiniteScrollDiv');
    if (navigationPath === window.location.pathname && div) {
      div.scrollTo(0, 0);
      setReloadRfps(true);
    }
    history.push(`${navigationPath}`);
    setSelectedIndex(getSelectedIndexFromCurrentUrl());
    closeMobileDrawer();
  };

  const handleProfileListItemClick = (navigationPath: string) => {
    history.push(`${navigationPath}`);
    setSelectedIndex(getSelectedIndexFromCurrentUrl());
    closeMobileDrawer();
  };

  const handleCreateProjectClick = () => {
    history.push('/create-project');
  };

  const handleLogoutClick = () => {
    signOut(firebaseAuth)
      .then(() => {
        history.push('/');
        sessionStorage.clear();
        localStorage.clear();
        setUser(null as any);
        setSavedProjectsFilter(false);
        closeMobileDrawer();
      })
      .catch((err) => {
        process.env.NODE_ENV === 'development' && console.error(err);
        Sentry.withScope(function (scope) {
          scope.setTag('section', 'LeftNavigation.tsx handleLogoutClick()');
          Sentry.captureException(err);
        });
      });
  };

  const getMenuItems = () => {
    if (isHomeowner(user) || isRealtor(user)) {
      return (
        <ItemsHomeowner
          user={user}
          width={props.width}
          selectedIndex={selectedIndex}
          handleListItemClick={handleListItemClick}
          handleProfileListItemClick={handleProfileListItemClick}
          handleCreateProjectClick={handleCreateProjectClick}
          handleLogoutClick={handleLogoutClick}
        />
      );
    } else if (isContractor(user)) {
      return (
        <ItemsContractor
          user={user}
          width={props.width}
          selectedIndex={selectedIndex}
          handleListItemClick={handleListItemClick}
          handleProfileListItemClick={handleProfileListItemClick}
          handleLogoutClick={handleLogoutClick}
          setDrawerOpen={props.setDrawerOpen}
        />
      );
    } else {
      return (
        <ItemsAnonymous
          width={props.width}
          selectedIndex={selectedIndex}
          handleListItemClick={handleListItemClick}
          handleClickCreateProject={handleCreateProjectClick}
        />
      );
    }
  };

  return (
    <NavigationDrawer
      width={width}
      hidden={removeHeader}
      open={props.drawerOpen}
      setOpen={props.setDrawerOpen}>
      {getMenuItems()}
    </NavigationDrawer>
  );
};
